import { useTranslation } from 'react-i18next'
import { Alert, Button, CircularProgress, List, ListItemButton, ListItemIcon, ListItemText, Stack, Typography } from '@mui/material'
import BluetoothSearchingIcon from '@mui/icons-material/BluetoothSearching'
import SignalCellular1BarIcon from '@mui/icons-material/SignalCellular1Bar'
import SignalCellular3BarIcon from '@mui/icons-material/SignalCellular3Bar'
import SignalCellular4BarIcon from '@mui/icons-material/SignalCellular4Bar'

import { ConnectorBleDevice } from '@/constants/medconnect-devices-types'

import { BluetoothScanState } from '../../model/useBluetoothScan'

export type BluetoothUnitPickerProps = {
  scan: BluetoothScanState
  selectedId: string | undefined
  onSelect: (id: string | undefined) => void
}

const STRONG_SIGNAL_DBM = -60
const FAIR_SIGNAL_DBM = -75

/** Live list of matching Bluetooth units; picking one pins the device to that unit. */
export default function BluetoothUnitPicker({ scan, selectedId, onSelect }: BluetoothUnitPickerProps) {
  const { t } = useTranslation()
  const { devices, isScanning, hasScanned, rescan } = scan

  return (
    <Stack spacing={1.5}>
      <Alert severity="info" icon={<BluetoothSearchingIcon />}>{t('page.configuration.MedconnectDevice.wizard.bluetooth.hint')}</Alert>
      <Stack direction="row" alignItems="center" spacing={1}>
        {isScanning && <CircularProgress size={16} />}
        <Typography variant="body2" color="text.secondary">
          {isScanning ? t('page.configuration.MedconnectDevice.wizard.bluetooth.scanning') : t('page.configuration.MedconnectDevice.wizard.bluetooth.found', { count: devices.length })}
        </Typography>
        <Button size="small" onClick={rescan} disabled={isScanning}>{t('page.configuration.MedconnectDevice.wizard.bluetooth.rescan')}</Button>
      </Stack>
      {hasScanned && devices.length === 0 && !isScanning && (
        <Alert severity="warning">{t('page.configuration.MedconnectDevice.wizard.bluetooth.noneFound')}</Alert>
      )}
      <List dense disablePadding>
        <ListItemButton selected={selectedId === undefined} onClick={() => onSelect(undefined)}>
          <ListItemText primary={t('page.configuration.MedconnectDevice.wizard.bluetooth.anyUnit')} secondary={t('page.configuration.MedconnectDevice.wizard.bluetooth.anyUnitHint')} />
        </ListItemButton>
        {devices.map((device) => (
          <ListItemButton key={device.id} selected={selectedId === device.id} onClick={() => onSelect(device.id)}>
            <ListItemIcon>{renderSignalIcon(device)}</ListItemIcon>
            <ListItemText primary={device.name} secondary={device.rssi === null ? device.id : `${device.rssi} dBm · ${device.id}`} />
          </ListItemButton>
        ))}
      </List>
    </Stack>
  )
}

function renderSignalIcon(device: ConnectorBleDevice): JSX.Element {
  if (device.rssi === null || device.rssi < FAIR_SIGNAL_DBM) return <SignalCellular1BarIcon color="disabled" />
  if (device.rssi < STRONG_SIGNAL_DBM) return <SignalCellular3BarIcon color="action" />
  return <SignalCellular4BarIcon color="success" />
}
